import type { MonetaryAmount } from '../api/common.js';
import type { CertificateEditPayload, CertificatePayload } from '../api/requests.js';
import type { CertificateListResponse, CertificateResponse } from '../api/responses.js';
import { formatDateForRequest, normalizeInstant } from '../dates.js';
import type {
  CertificateDetails,
  CertificateInput,
  CertificateList,
  CertificateRow,
  CertificateSearchParams,
} from '../models.js';

export const toCertificateRow = (certificate: CertificateResponse): CertificateRow => ({
  id: certificate.id,
  policyNumber: certificate.policyNumber,
  certificateIssuedTo: certificate.certificateIssuedTo,
  countryName: certificate.countryName,
  createdBy: certificate.createdBy?.userName ?? '',
  createdAt: normalizeInstant(certificate.createdAt),
  currentStatus: certificate.currentStatus,
});

const firstCurrency = (...amounts: (MonetaryAmount | null | undefined)[]): string =>
  amounts.find((amount) => amount?.currency)?.currency ?? '';

export const toCertificateDetails = (certificate: CertificateResponse): CertificateDetails => {
  const storageLocations = certificate.storageLocations ?? [];
  const history = certificate.statusHistory ?? [];

  return {
    id: certificate.id,
    templateName: certificate.templateName,
    currentStatus: certificate.currentStatus || history[history.length - 1]?.toStatus || 'DRAFT',
    certificateIssuedTo: certificate.certificateIssuedTo,
    customerAddress: certificate.customerAddress,
    country: certificate.countryName || certificate.countryCode,
    policyNumber: certificate.policyNumber,
    policyYear: certificate.policyYear ?? null,
    policyExpirationDate: normalizeInstant(certificate.policyExpirationDate),
    contractingEntity: certificate.contractingEntity,
    currency: firstCurrency(
      certificate.propertyOnBoardLimit,
      certificate.transitLimit,
      certificate.atmLimit,
      certificate.compusafeLimit,
      certificate.moneyProcessingLimit,
      ...storageLocations,
    ),
    transitLimit: certificate.transitLimit ?? null,
    storageLocations,
    additionalComments: certificate.additionalComments ?? '',
    createdAt: normalizeInstant(certificate.createdAt),
    createdBy: certificate.createdBy?.userName || certificate.createdBy?.email || '',
  };
};

export const toCertificateList = (
  response: CertificateListResponse | undefined,
): CertificateList => ({
  rows: (response?.certificates.content ?? []).map(toCertificateRow),
  totalElements: response?.certificates.totalElements ?? 0,
  totalPages: response?.certificates.totalPages ?? 1,
  countsByStatus: response?.countsByStatus ?? {},
});

export const toCertificateSearchQuery = ({
  page,
  size,
  sort,
  status,
  query,
  excludeDeleted,
}: CertificateSearchParams): URLSearchParams => {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
  });

  if (sort) {
    params.set('sort', `${sort.field},${sort.desc ? 'desc' : 'asc'}`);
  }
  if (query?.trim()) {
    params.set('query', query.trim());
  }
  if (status) {
    params.set('status', status);
  }
  if (excludeDeleted !== undefined) {
    params.set('excludeDeleted', String(excludeDeleted));
  }

  return params;
};

const toMonetary = (value: number | undefined, currency: string): MonetaryAmount => ({
  currency,
  amount: value ?? 0,
});

const toInstant = (date: string, time: string): string | null =>
  date ? new Date(`${date}T${time}Z`).toISOString() : null;

export const buildCertificatePayload = (
  input: CertificateInput,
  captivePolicyId: string,
): CertificatePayload => {
  const currency = input.currency || 'USD';

  return {
    certificateTemplateId: input.certificateTemplateId,
    captivePolicyId,

    certificateIssuedTo: input.certificateIssuedTo,
    customerAddress: input.customerAddress,
    countryCode: input.countryCode,

    effectiveDate: toInstant(formatDateForRequest(input.effectiveDate), '00:00:00.000'),
    policyExpirationDate: toInstant(
      formatDateForRequest(input.policyExpirationDate),
      '23:59:59.999',
    ),
    policyNumber: input.policyNumber,
    currency,
    contractingEntity: input.contractingEntity,

    transitLimit: toMonetary(input.transitLimit || input.propertyOnBoardLimit, currency),
    atmLimit: toMonetary(input.atmLimit, currency),
    compusafeLimit: toMonetary(input.compusafeLimit, currency),
    moneyProcessingLimit: toMonetary(input.moneyProcessingLimit, currency),

    storageLocations: input.storageLocations
      .filter(({ storageLocation }) => storageLocation)
      .map(({ storageLocation, limitStorage }) => ({
        storageLocation,
        ...toMonetary(limitStorage, currency),
      })),

    additionalComments: input.additionalComments,
    situation: input.situation,
    sanctionedCountryTouchpoint: input.sanctionedCountryTouchpoint,
    isInternal: false,
  };
};

export const buildCertificateEditPayload = (
  certificate: CertificateResponse,
  input: CertificateInput,
  captivePolicyId: string,
): CertificateEditPayload => ({
  ...buildCertificatePayload(input, captivePolicyId),
  id: certificate.id,
  certificateNumber: certificate.certificateNumber ?? null,
  tenantId: certificate.tenantId,
  subPolicyId: certificate.subPolicyId ?? null,
  currentStatus: certificate.currentStatus,
});
